import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { X, Save, Plus, Edit, Trash2, Tag, Package } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available icons for categories
const iconOptions = [
  'Wheat', 'Cookie', 'Droplet', 'Coffee', 'Package', 'Sparkles', 
  'Apple', 'Beef', 'Fish', 'ShoppingBag', 'Tag'
];

const CategoryManager = ({ isOpen, onClose, onCategoriesUpdated }) => {
  const { getAuthHeader, getStoreId } = useAuth();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCategory, setEditingCategory] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [formData, setFormData] = useState({
    id: '',
    name: '',
    icon: 'Package'
  });

  useEffect(() => {
    if (isOpen) {
      loadCategories();
    }
  }, [isOpen]);

  const loadCategories = async () => {
    try {
      const storeId = getStoreId();
      const response = await axios.get(`${API}/categories?store_id=${storeId}`);
      setCategories(response.data);
    } catch (error) {
      toast.error('Error al cargar categorías');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingCategory) {
        // Update
        await axios.put(
          `${API}/categories/${editingCategory.id}`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Categoría actualizada');
      } else {
        // Create
        await axios.post(
          `${API}/categories`,
          formData,
          { headers: getAuthHeader() }
        );
        toast.success('Categoría creada');
      }

      resetForm();
      loadCategories();
      onCategoriesUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar categoría');
    }
  };

  const handleEdit = (category) => {
    setEditingCategory(category);
    setFormData({
      id: category.id,
      name: category.name,
      icon: category.icon
    });
  };

  const handleDelete = async (categoryId, categoryName) => {
    setDeleteConfirm({ id: categoryId, name: categoryName });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm) return;
    try {
      await axios.delete(
        `${API}/categories/${deleteConfirm.id}`,
        { headers: getAuthHeader() }
      );
      toast.success('Categoría eliminada');
      setDeleteConfirm(null);
      loadCategories();
      onCategoriesUpdated();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al eliminar categoría');
      setDeleteConfirm(null);
    }
  };

  const resetForm = () => {
    setEditingCategory(null);
    setFormData({
      id: '',
      name: '',
      icon: 'Package'
    });
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Header */}
          <div className="p-6 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
            <div className="flex items-center gap-2">
              <Tag className="text-accent-text" size={24} />
              <h2 className="heading-3">Gestión de Categorías</h2>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
              <X size={24} />
            </button>
          </div>

          <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Form */}
            <div>
              <h3 className="font-semibold text-lg mb-4">
                {editingCategory ? 'Editar Categoría' : 'Nueva Categoría'}
              </h3>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">ID de Categoría *</label>
                  <input
                    type="text"
                    value={formData.id}
                    onChange={(e) => setFormData({ ...formData, id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
                    required
                    disabled={!!editingCategory}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'var(--border-light)',
                      '--tw-ring-color': 'var(--accent-primary)',
                      opacity: editingCategory ? 0.6 : 1
                    }}
                    placeholder="ej: frutas"
                  />
                  <p className="text-xs text-text-muted mt-1">Usar minúsculas y guiones bajos</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Nombre *</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'var(--border-light)',
                      '--tw-ring-color': 'var(--accent-primary)'
                    }}
                    placeholder="ej: Frutas"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Icono *</label>
                  <select
                    value={formData.icon}
                    onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                    className="w-full px-4 py-2 rounded-lg border focus:outline-none focus:ring-2"
                    style={{
                      borderColor: 'var(--border-light)',
                      '--tw-ring-color': 'var(--accent-primary)'
                    }}
                  >
                    {iconOptions.map(icon => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </div>

                <div className="flex gap-2">
                  <button type="submit" className="btn-primary flex-1">
                    <Save size={18} className="inline mr-2" />
                    {editingCategory ? 'Actualizar' : 'Crear'}
                  </button>
                  {editingCategory && (
                    <button type="button" onClick={resetForm} className="btn-secondary">
                      Cancelar
                    </button>
                  )}
                </div>
              </form>
            </div>

            {/* List */}
            <div>
              <h3 className="font-semibold text-lg mb-4">Categorías Existentes</h3>
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {categories.map((category) => (
                  <div
                    key={category.id}
                    className="flex items-center justify-between p-3 rounded-lg border hover:bg-section transition-colors"
                    style={{ borderColor: 'var(--border-light)' }}
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" 
                           style={{ background: 'var(--accent-wash)' }}>
                        <span className="text-xs font-medium text-accent-text">{category.icon[0]}</span>
                      </div>
                      <div>
                        <p className="font-medium text-text-primary">{category.name}</p>
                        <p className="text-xs text-text-muted">{category.id}</p>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <button
                        onClick={() => handleEdit(category)}
                        className="p-2 hover:bg-accent-wash rounded-lg"
                        title="Editar"
                      >
                        <Edit size={16} className="text-accent-text" />
                      </button>
                      <button
                        onClick={() => handleDelete(category.id, category.name)}
                        className="p-2 hover:bg-red-50 rounded-lg"
                        title="Eliminar"
                      >
                        <Trash2 size={16} className="text-red-500" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="fixed inset-0 bg-black bg-opacity-50" onClick={() => setDeleteConfirm(null)} />
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full relative z-10" data-testid="cat-delete-confirm-modal">
            <div className="text-center mb-4">
              <Trash2 size={40} className="mx-auto text-red-500 mb-3" />
              <h3 className="font-bold text-lg text-text-primary">Eliminar Categoría</h3>
              <p className="text-text-muted mt-2">
                ¿Estás seguro de eliminar <strong>"{deleteConfirm.name}"</strong>?
              </p>
              <p className="text-xs text-text-muted mt-1">Si tiene productos asignados, no podrá ser eliminada.</p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="btn-secondary flex-1"
                data-testid="cat-delete-cancel-btn"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 rounded-lg text-white font-medium transition-colors"
                style={{ background: '#ef4444' }}
                data-testid="cat-delete-confirm-btn"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CategoryManager;
